import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';

import { CharterDetailsGetRes, CharterReservationReqBody, fetchCharterDetails } from '~/services/CharterService';
import { findReservationByPNR, updateReservationAddons } from '~/services/FlightsService';
import type { UpdateReservationAddonsPayload, UpdateReservationAddonsResponse } from '~/services/FlightsService';

import {
  CharterCabinClass,
  CharterDetails,
  CharterFlight,
  SeatInfo,
  SeatLegendItem,
  SeatMapRow,
  SeatSelectionState,
  Traveller,
} from '../charter.types';
import { CHARTER_SEAT_CLEAR_BLOCKED_MESSAGE } from '../common/charterSeatMessages';
import { deriveCharterDetails } from '../common/deriveCharterDetails';
import { determineSeatAvailability } from '../common/determineSeatAvailability';
import { generateSeatMap } from '../common/generateSeatMap';
import { seatLegend } from '../common/seatLegend';

type CharterSeatsAddonItem = Extract<UpdateReservationAddonsPayload['addons'][number], { addonType: 'SEATS' }>;

type SvcFlightsBookingJourneyFlight = {
  id: string;
  flightNumber?: string | null;
  departingDate?: string | null;
  departureAirport?: string | null;
  arrivalAirport?: string | null;
};

type ReservationFlightMatchResult = {
  flight?: SvcFlightsBookingJourneyFlight;
  error?: string;
};
function getMatchingReservationFlight(
  charterFlight: CharterFlight | undefined,
  reservationFlights: Array<SvcFlightsBookingJourneyFlight>,
): ReservationFlightMatchResult {
  if (!charterFlight) {
    return { error: 'Seat selections cannot be saved: charter flight details are missing.' };
  }

  const matches = reservationFlights.filter(
    (flight) =>
      flight.flightNumber === charterFlight.flightNumber &&
      flight.departingDate === charterFlight.depDate &&
      flight.departureAirport === charterFlight.depAirportCode &&
      flight.arrivalAirport === charterFlight.arrAirportCode,
  );

  if (matches.length === 1) {
    return { flight: matches[0] };
  }

  const flightLabel = `${charterFlight?.flightNumber ?? '—'} (${charterFlight?.depAirportCode ?? '—'} → ${
    charterFlight?.arrAirportCode ?? '—'
  }, ${charterFlight?.depDate ?? '—'})`;
  if (matches.length > 1) {
    return {
      error:
        `Seat selections cannot be saved: charter flight ${flightLabel} matches multiple ` +
        'svc-flights booking-journey segments. Update the reservation or contact support.',
    };
  }

  return {
    error:
      `Seat selections cannot be saved: charter flight ${flightLabel} ` +
      'does not match any flight on this reservation’s booking journey in svc-flights. ' +
      'Update the reservation or contact support.',
  };
}

/** Normalise for comparing persisted addon seat vs desired seat (skip API when unchanged). */
function normaliseSeatNumberForSync(value: string | undefined | null): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) {
    return undefined;
  }
  return trimmed.toUpperCase();
}

function charterFlightHasSeatAssignment(
  charterDetails: CharterDetails,
  seatSelections: SeatSelectionState,
  charterFlight: CharterFlight,
): boolean {
  return charterDetails.travellers.some((traveller) => Boolean(seatSelections[charterFlight.id]?.[traveller.id]));
}

function getReservationFlightMismatchMessage(
  charterDetails: CharterDetails,
  seatSelections: SeatSelectionState,
  reservation: Awaited<ReturnType<typeof findReservationByPNR>>,
): string | null {
  const reservationFlights =
    reservation.booking_journey?.legs.flatMap((leg) => leg.flights as Array<SvcFlightsBookingJourneyFlight>) ?? [];
  for (const flight of charterDetails.flights) {
    if (!charterFlightHasSeatAssignment(charterDetails, seatSelections, flight)) {
      continue;
    }
    const { error } = getMatchingReservationFlight(flight, reservationFlights);
    if (error) {
      return error;
    }
  }
  return null;
}

// LUX_ROW seat pairing: E and F must be selected together in the same row
const LUX_ROW_SEAT_PAIRS: Record<string, string> = { E: 'F', F: 'E' };

const validateLuxRowPairing = (
  flightId: string,
  cabinClass: CharterCabinClass,
  seatSelections: SeatSelectionState,
): { unpairedSeats: Set<string>; isValid: boolean } => {
  if (cabinClass.toUpperCase() !== 'LUX_ROW') {
    return { unpairedSeats: new Set(), isValid: true };
  }

  const selectedSeats = Object.values(seatSelections[flightId] ?? {}).filter(
    (seat): seat is string => typeof seat === 'string',
  );
  const seatsByRow = new Map<string, Set<string>>();

  for (const seatId of selectedSeats) {
    const row = seatId.slice(0, -1);
    const col = seatId.slice(-1);
    const existingCols = seatsByRow.get(row);
    if (existingCols) {
      existingCols.add(col);
    } else {
      seatsByRow.set(row, new Set([col]));
    }
  }

  const unpairedSeats = new Set<string>();
  for (const [row, cols] of seatsByRow) {
    for (const col of cols) {
      const requiredPartner = LUX_ROW_SEAT_PAIRS[col];
      if (requiredPartner && !cols.has(requiredPartner)) {
        unpairedSeats.add(`${row}${col}`);
      }
    }
  }

  return { unpairedSeats, isValid: unpairedSeats.size === 0 };
};

export type UseCharterSeatManagementParams = {
  initialDetails?: CharterDetails;
  initialPnr?: string;
};

export type UseCharterSeatManagementReturn = {
  pnr: string;
  setPnr: (value: string) => void;
  charterDetails: CharterDetails | null;
  seatSelections: SeatSelectionState;
  isSearching: boolean;
  searchError: string | null;
  seatPaymentCollected: boolean;
  setSeatPaymentCollected: (value: boolean) => void;
  seatPaymentReference: string;
  setSeatPaymentReference: (value: string) => void;
  isSaving: boolean;
  saveError: string | null;
  saveConfirmation: string | null;
  handleSearch: () => void;
  handleSeatChange: (travellerId: string, flightId: string, seatId?: string) => void;
  availableSeatOptions: (traveller: Traveller, flight: CharterFlight, travellers: Array<Traveller>) => Array<SeatInfo>;
  handleSave: () => void;
  seatPriceForSelection: (flightId: string, seatId?: string) => number | undefined;
  seatLegendItems: Array<SeatLegendItem>;
  selectedSeatsPrices: Array<number>;
  getSelectionError: (traveller: Traveller, flight: CharterFlight) => string | null;
  hasInvalidSelections: boolean;
  /** True when svc-flights booking journey cannot be aligned with charter legs (or reservation fetch failed). */
  reservationSyncBlocksSave: boolean;
};

const generateSeatLookup = (rows: Array<SeatMapRow>): Record<string, SeatInfo> => {
  const lookup: Record<string, SeatInfo> = {};

  rows.forEach((row) => {
    row.seats.forEach((seat) => {
      const seatKey = `${seat.cabin}:${seat.primaryColumn ?? seat.seatId}`;
      lookup[seatKey] = seat;
    });
  });

  return lookup;
};
const getSeatLookupKey = (cabin: string, seatId: string): string => `${cabin}:${seatId}`;

export const useCharterSeatManagement = ({
  initialDetails,
  initialPnr,
}: UseCharterSeatManagementParams = {}): UseCharterSeatManagementReturn => {
  const normalisedInitialPnr = useMemo(() => initialPnr?.trim().toUpperCase() ?? '', [initialPnr]);

  const [pnr, setPnr] = useState(normalisedInitialPnr);
  const [activePnr, setActivePnr] = useState<string | null>(normalisedInitialPnr || null);
  const [charterDetails, setCharterDetails] = useState<CharterDetails | null>(null);
  const [seatSelections, setSeatSelections] = useState<SeatSelectionState>({});
  const [seatPaymentCollected, setSeatPaymentCollected] = useState(false);
  const [seatPaymentReference, setSeatPaymentReference] = useState('');
  const [saveConfirmation, setSaveConfirmation] = useState<string | null>(null);
  const [clientValidationError, setClientValidationError] = useState<string | null>(null);

  const seatLookup = useMemo(() => {
    const flights = charterDetails?.flights ?? initialDetails?.flights;
    if (!flights) {
      return {} as Record<string, Record<string, SeatInfo>>;
    }

    const byFlight: Record<string, Record<string, SeatInfo>> = {};
    flights.forEach((flight) => {
      const rows = flight.cabinLayout.length ? flight.cabinLayout : generateSeatMap(flight.seatConfiguration);
      byFlight[flight.id] = generateSeatLookup(rows);
    });
    return byFlight;
  }, [charterDetails, initialDetails]);

  const {
    data,
    isFetching,
    error: searchErrorRaw,
    refetch,
  } = useQuery<CharterDetailsGetRes['result']>({
    queryKey: ['charterSeats', activePnr],
    queryFn: async () => {
      if (!activePnr) {
        throw new Error('PNR is required');
      }
      return await fetchCharterDetails(activePnr);
    },
    enabled: activePnr !== null,
    staleTime: 0,
  });

  useEffect(() => {
    if (!normalisedInitialPnr) {
      return;
    }

    setPnr(normalisedInitialPnr);
    setActivePnr(normalisedInitialPnr);
  }, [normalisedInitialPnr]);

  useEffect(() => {
    if (!data) {
      return;
    }

    const charterDetails = deriveCharterDetails(data);

    setCharterDetails(charterDetails);
    setSeatSelections(data.seatSelections ?? {});
    setSeatPaymentCollected(data.seatPaymentCollected);
    setSeatPaymentReference(data.seatPaymentReference ?? '');
    setClientValidationError(null);
    setSaveConfirmation(null);
  }, [data]);

  const { data: reservationForSeatSync, isError: isReservationForSeatSyncError } = useQuery({
    queryKey: ['charterSeatReservationForSync', charterDetails?.pnr],
    queryFn: async () => {
      const syncPnr = charterDetails?.pnr;
      if (!syncPnr) {
        throw new Error('PNR is required');
      }
      return await findReservationByPNR(syncPnr);
    },
    enabled: Boolean(charterDetails?.pnr),
    staleTime: 30_000,
  });

  const reservationSyncIssue = useMemo(() => {
    if (!charterDetails?.pnr) {
      return { blocksSave: false, message: null as string | null };
    }
    if (isReservationForSeatSyncError) {
      return {
        blocksSave: true,
        message:
          'Could not load this reservation from svc-flights. Seat selections cannot be saved until the reservation loads successfully.',
      };
    }
    if (!reservationForSeatSync) {
      return { blocksSave: false, message: null };
    }
    const mismatch = getReservationFlightMismatchMessage(charterDetails, seatSelections, reservationForSeatSync);
    return mismatch ? { blocksSave: true, message: mismatch } : { blocksSave: false, message: null };
  }, [charterDetails, seatSelections, isReservationForSeatSyncError, reservationForSeatSync]);

  const saveMutation = useMutation({
    mutationFn: async (payload: CharterReservationReqBody & { pnr: string }) => {
      // updateReservationAddons: seat state comes from `addons` (SEATS rows; clears via cancelled).
      // `addonData.flightId` is svc-flights segment id: `booking_journey.legs[].flights[].id` (matched from charter flight), not leg id or charter `flightInstanceId`. Empty addons → local no-op.
      const reservation = await findReservationByPNR(payload.pnr);
      const seatAddons = (reservation.reservation_addons ?? []).filter((addon) => addon.addon_type === 'SEATS');
      const activeSeatAddons = seatAddons.filter((addon) => addon.status?.toLowerCase() !== 'cancelled');
      const reservationFlights =
        reservation.booking_journey?.legs.flatMap((leg) => leg.flights as Array<SvcFlightsBookingJourneyFlight>) ?? [];

      const travellers = (charterDetails?.travellers ?? []).map((t) => ({
        travellerId: t.id,
        firstName: t.firstName,
        middleName: t.middleName,
        lastName: t.lastName,
        dietaryRequirements: t.dietaryRequirements,
      }));

      // Use `null` for unassigned slots so JSON.stringify keeps the key (undefined is omitted).
      const providerDataSeatSelections = (payload.seatSelections ?? []).map((row) => ({
        travellerId: row.travellerId,
        flightInstanceId: row.flightInstanceId,
        seatNumber: row.seatNumber ?? null,
        price: row.price,
      }));

      const providerData = {
        provider: 'charter' as const,
        contactEmail: payload.contactEmail,
        contactPhone: payload.contactPhone,
        travellers,
        seatSelections: providerDataSeatSelections,
        seatPaymentCollected: payload.seatPaymentCollected,
        seatPaymentReference: payload.seatPaymentReference,
      };

      const findActiveSeatAddon = (travellerId: string, flightInstanceId: string) => {
        const charterFlight = charterDetails?.flights.find((flight) => flight.id === flightInstanceId);
        const matchingReservationFlight = getMatchingReservationFlight(charterFlight, reservationFlights).flight;
        return activeSeatAddons.find(
          (addon) =>
            addon.travellerId === travellerId &&
            (addon.flightId === flightInstanceId ||
              (matchingReservationFlight !== undefined && addon.flightId === matchingReservationFlight.id)),
        );
      };

      const addons: Array<CharterSeatsAddonItem> = [];

      for (const sel of payload.seatSelections ?? []) {
        const existingAddon = findActiveSeatAddon(sel.travellerId, sel.flightInstanceId);
        const previousSeat = normaliseSeatNumberForSync(existingAddon?.seatNumber);
        const currentSeat = normaliseSeatNumberForSync(sel.seatNumber);

        if (previousSeat === currentSeat) {
          continue;
        }

        if (!currentSeat) {
          // API requires a non-empty `addonData.seatNumber` string; omit cancel if addon data is incomplete.
          const seatNumberForCancel = existingAddon?.seatNumber?.trim();
          if (existingAddon?.id && seatNumberForCancel) {
            addons.push({
              addonId: existingAddon.id,
              addonType: 'SEATS',
              status: 'cancelled',
              addonData: {
                flightId: existingAddon.flightId,
                travellerId: sel.travellerId,
                pnrId: payload.pnr,
                seatNumber: seatNumberForCancel,
              },
            });
          }
          continue;
        }

        const seatNumberForApi = sel.seatNumber?.trim();
        if (!seatNumberForApi) {
          continue;
        }

        const charterFlight = charterDetails?.flights.find((flight) => flight.id === sel.flightInstanceId);
        const { flight: matchingReservationFlight, error: matchingReservationFlightError } =
          getMatchingReservationFlight(charterFlight, reservationFlights);
        if (matchingReservationFlightError) {
          throw new Error(matchingReservationFlightError);
        }
        const flightId = matchingReservationFlight.id;

        addons.push({
          addonId: existingAddon?.id,
          addonType: 'SEATS',
          addonData: {
            flightId,
            travellerId: sel.travellerId,
            pnrId: payload.pnr,
            seatNumber: seatNumberForApi,
          },
          providerCost: sel.price,
        });
      }

      if (addons.length === 0) {
        const noop: UpdateReservationAddonsResponse = {
          status: 200,
          message: 'No seat changes to save.',
          result: { updatedAddonIds: [], notFoundAddonIds: [] },
        };
        return noop;
      }

      return await updateReservationAddons({ reservationId: reservation.id, addons, providerData });
    },
    onSuccess: (response) => {
      setSaveConfirmation(response.message ?? 'Seats updated successfully');
    },
    onError: () => {
      setSaveConfirmation(null);
    },
  });

  const handleSearch = useCallback(() => {
    const trimmed = pnr.trim().toUpperCase();
    if (!trimmed) {
      return;
    }

    setActivePnr((prev) => {
      if (prev === trimmed) {
        void refetch();
        return prev;
      }

      return trimmed;
    });
  }, [pnr, refetch]);

  const handleSeatChange = useCallback(
    (travellerId: string, flightId: string, seatId?: string) => {
      const clearing = seatId == null || seatId === '';
      if (clearing) {
        const current = seatSelections[flightId]?.[travellerId];
        if (typeof current === 'string' && current.length > 0) {
          setClientValidationError(CHARTER_SEAT_CLEAR_BLOCKED_MESSAGE);
          return;
        }
      }

      setSeatSelections((prev) => {
        let next: SeatSelectionState = {
          ...prev,
          [flightId]: {
            ...prev[flightId],
            [travellerId]: seatId ?? null,
          },
        };

        // Drop selections that no longer pass adjacency / availability rules (e.g. after parent change).
        const flight = charterDetails?.flights.find((f) => f.id === flightId);
        const travellers = charterDetails?.travellers ?? [];

        if (flight && travellers.length) {
          const flightSelections = { ...(next[flightId] ?? {}) };

          travellers.forEach((t) => {
            const assignedSeatId = flightSelections[t.id] ?? null;
            if (!assignedSeatId) {
              return;
            }

            const lookupKey = getSeatLookupKey(flight.cabinClass, assignedSeatId);
            const seatInfo = seatLookup[flight.id]?.[lookupKey];
            if (!seatInfo) {
              return;
            }

            const isAllowed = determineSeatAvailability(
              seatInfo,
              t,
              travellers,
              next,
              flight.id,
              flight.seatConfiguration,
            );

            if (!isAllowed) {
              flightSelections[t.id] = null;
            }
          });

          next = {
            ...next,
            [flightId]: flightSelections,
          };
        }

        return next;
      });
      setClientValidationError(null);
      setSaveConfirmation(null);
    },
    [charterDetails, seatLookup, seatSelections],
  );

  const clearConfirmationAndSetSeatPaymentCollected = useCallback((value: boolean) => {
    setSeatPaymentCollected(value);
    setClientValidationError(null);
    setSaveConfirmation(null);
  }, []);

  const clearConfirmationAndSetSeatPaymentReference = useCallback((value: string) => {
    setSeatPaymentReference(value);
    setClientValidationError(null);
    setSaveConfirmation(null);
  }, []);

  const seatAssignedElsewhere = useCallback(
    (flightId: string, seatId: string, travellerId: string) => {
      const byTraveller = seatSelections[flightId] ?? {};
      return Object.entries(byTraveller).some(
        ([otherTravellerId, selectedSeat]) => otherTravellerId !== travellerId && selectedSeat === seatId,
      );
    },
    [seatSelections],
  );

  const availableSeatOptions = useCallback(
    (traveller: Traveller, flight: CharterFlight, travellers: Array<Traveller>) => {
      const rows = flight.cabinClass
        ? flight.cabinLayout.filter((row) => row.cabin === flight.cabinClass)
        : flight.cabinLayout;

      return rows
        .flatMap((row) => row.seats)
        .filter((seat) =>
          determineSeatAvailability(seat, traveller, travellers, seatSelections, flight.id, flight.seatConfiguration),
        )
        .filter((seat) => !seatAssignedElsewhere(flight.id, seat.primaryColumn ?? seat.seatId, traveller.id))
        .sort((a, b) => a.seatId.localeCompare(b.seatId, undefined, { numeric: true }));
    },
    [seatAssignedElsewhere, seatSelections],
  );

  const seatPriceForSelection = useCallback(
    (flightId: string, cabin: CharterCabinClass, seatId?: string) => {
      if (!seatId) {
        return undefined;
      }

      const lookupKey = getSeatLookupKey(cabin, seatId);
      return seatLookup[flightId]?.[lookupKey]?.price ?? 0;
    },
    [seatLookup],
  );

  const selectedSeatsPrices = useMemo(() => {
    if (!charterDetails) {
      return [];
    }

    return Object.entries(seatSelections).flatMap(([flightId, flightSelections]) => {
      const flight = charterDetails.flights.find((f) => f.id === flightId);
      if (!flight) {
        return [];
      }

      return Object.values(flightSelections)
        .filter((seatId): seatId is string => Boolean(seatId))
        .map((seatId) => {
          const lookupKey = getSeatLookupKey(flight.cabinClass, seatId);
          return seatLookup[flightId]?.[lookupKey]?.price ?? 0;
        });
    });
  }, [seatSelections, seatLookup, charterDetails]);

  useEffect(() => {
    const allSeatsFree = selectedSeatsPrices.length > 0 && selectedSeatsPrices.every((price) => price === 0);

    if (allSeatsFree) {
      setSeatPaymentCollected(false);
      setSeatPaymentReference('');
    }
  }, [selectedSeatsPrices, setSeatPaymentCollected, setSeatPaymentReference]);

  const handleSave = useCallback(() => {
    if (!charterDetails) {
      return;
    }

    const allSeatsFree = selectedSeatsPrices.length > 0 && selectedSeatsPrices.every((price) => price === 0);

    const trimmedPaymentReference = seatPaymentReference.trim();
    if (seatPaymentCollected && !allSeatsFree && !trimmedPaymentReference) {
      setClientValidationError('Payment reference is required when payment is collected.');
      return;
    }

    const travellers = charterDetails.travellers;
    for (const flight of charterDetails.flights) {
      const byTraveller = seatSelections[flight.id] ?? {};
      for (const t of travellers) {
        const assignedSeatId = byTraveller[t.id] ?? null;
        if (!assignedSeatId) {
          continue;
        }
        const lookupKey = getSeatLookupKey(flight.cabinClass, assignedSeatId);
        const seatInfo = seatLookup[flight.id]?.[lookupKey];
        if (!seatInfo) {
          continue;
        }
        const isAllowed = determineSeatAvailability(
          seatInfo,
          t,
          travellers,
          seatSelections,
          flight.id,
          flight.seatConfiguration,
        );
        if (!isAllowed) {
          setClientValidationError('Invalid seat selections: one or more seats violate seating rules.');
          return;
        }
      }
    }

    const payload: CharterReservationReqBody & { pnr: string } = {
      pnr: charterDetails.pnr,
      seatPaymentCollected: allSeatsFree ? false : seatPaymentCollected,
      seatPaymentReference: allSeatsFree ? undefined : trimmedPaymentReference,
      seatSelections: charterDetails.travellers.flatMap((traveller) =>
        charterDetails.flights.map((flight) => {
          const seatId = seatSelections[flight.id]?.[traveller.id] ?? undefined;
          const lookupKey = seatId ? getSeatLookupKey(flight.cabinClass, seatId) : undefined;
          const seatInfo = lookupKey ? seatLookup[flight.id]?.[lookupKey] : undefined;

          return {
            travellerId: traveller.id,
            flightInstanceId: flight.id,
            seatNumber: seatInfo?.primaryColumn ?? seatId, // API expects column letter when present
            price: seatInfo?.price ?? 0,
          };
        }),
      ),
    };

    setSaveConfirmation(null);
    setClientValidationError(null);
    saveMutation.mutate(payload);
  }, [
    charterDetails,
    seatPaymentCollected,
    seatPaymentReference,
    saveMutation,
    seatLookup,
    seatSelections,
    selectedSeatsPrices,
  ]);

  const getSelectionError = useCallback(
    (traveller: Traveller, flight: CharterFlight): string | null => {
      const byTraveller = seatSelections[flight.id] ?? {};
      const seatId = byTraveller[traveller.id] ?? null;
      if (!seatId) {
        return null;
      }

      const lookupKey = getSeatLookupKey(flight.cabinClass, seatId);
      const seatInfo = seatLookup[flight.id]?.[lookupKey];
      if (!seatInfo) {
        return 'Invalid seat selection';
      }

      const allowed = determineSeatAvailability(
        seatInfo,
        traveller,
        charterDetails?.travellers ?? [],
        seatSelections,
        flight.id,
        flight.seatConfiguration,
      );
      if (!allowed) {
        if (traveller.passengerType === 'INF') {
          return "Infants don't receive seat assignments.";
        }

        const seatLetter = seatId.slice(-1);
        const seatRow = seatId.slice(0, -1);
        const emergencySeatPartners = flight.seatConfiguration.emergencySeatPartners;
        const companionSeatLetter = emergencySeatPartners[seatLetter as keyof typeof emergencySeatPartners];
        const isEmergencyRowSeat = seatInfo.feature.isExitRow;
        const isEmergencySideSeat = isEmergencyRowSeat && Boolean(companionSeatLetter);

        if (traveller.passengerType === 'CHD' && isEmergencySideSeat) {
          const companionSeatId = `${seatRow}${companionSeatLetter}`;
          const hasAdultCompanion = (charterDetails?.travellers ?? [])
            .filter((t) => t.passengerType === 'ADT')
            .some((adult) => (seatSelections[flight.id]?.[adult.id] ?? null) === companionSeatId);
          if (!hasAdultCompanion) {
            return 'Child on emergency row side seat must have an adjacent adult.';
          }
        }

        const hasInfantInBooking = (charterDetails?.travellers ?? []).some((t) => t.passengerType === 'INF');
        if (traveller.passengerType === 'ADT' && hasInfantInBooking && isEmergencySideSeat) {
          return 'Adults cannot sit on emergency row side seats when an infant is in the booking.';
        }

        return 'Invalid seat selection';
      }

      if (flight.cabinClass.toUpperCase() === 'LUX_ROW') {
        const { unpairedSeats } = validateLuxRowPairing(flight.id, flight.cabinClass, seatSelections);
        if (unpairedSeats.has(seatId)) {
          const seatRow = seatId.slice(0, -1);
          const seatCol = seatId.slice(-1);
          const partnerCol = LUX_ROW_SEAT_PAIRS[seatCol];
          return `SkyLounge seats must be paired. Select ${seatRow}${partnerCol} for another traveller.`;
        }
      }

      return null;
    },
    [charterDetails, seatLookup, seatSelections],
  );

  const hasInvalidSelections = useMemo(() => {
    if (!charterDetails) {
      return false;
    }
    return charterDetails.travellers.some((traveller) =>
      charterDetails.flights.some((flight) => Boolean(getSelectionError(traveller, flight))),
    );
  }, [charterDetails, getSelectionError]);

  return {
    pnr,
    setPnr,
    charterDetails,
    seatSelections,
    isSearching: isFetching,
    searchError:
      searchErrorRaw instanceof Error ? searchErrorRaw.message : searchErrorRaw ? String(searchErrorRaw) : null,
    seatPaymentCollected,
    setSeatPaymentCollected: clearConfirmationAndSetSeatPaymentCollected,
    seatPaymentReference,
    setSeatPaymentReference: clearConfirmationAndSetSeatPaymentReference,
    isSaving: saveMutation.isPending,
    saveError:
      (reservationSyncIssue.blocksSave ? reservationSyncIssue.message : null) ??
      clientValidationError ??
      (saveMutation.error instanceof Error
        ? saveMutation.error.message
        : saveMutation.error
        ? String(saveMutation.error)
        : null),
    saveConfirmation,
    handleSearch,
    handleSeatChange,
    availableSeatOptions,
    handleSave,
    seatPriceForSelection,
    seatLegendItems: seatLegend,
    selectedSeatsPrices,
    getSelectionError,
    hasInvalidSelections,
    reservationSyncBlocksSave: reservationSyncIssue.blocksSave,
  };
};
